import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { HomeScreenScene } from '@/pages/onboarding/mockup/scenes/home-screen-scene';
import { LockScreenScene } from '@/pages/onboarding/mockup/scenes/lock-screen-scene';

export interface ChooseStoreMockupSceneProps {
  unlocked: boolean;
  appName: string | null;
  appIconUrl: string | null;
}

// Step 1's mockup: starts on LockScreenScene, then "unlocks" into
// HomeScreenScene once a store platform is picked — the lock fades/blurs
// away while the home screen scales up from underneath it, mimicking the
// real iOS unlock zoom. Kept as a single component (rather than switching
// IPhoneMockup's own `sceneKey`) so this specific transition can use its
// own richer variants instead of IPhoneMockup's generic cross-scene fade.
export function ChooseStoreMockupScene({
  unlocked,
  appName,
  appIconUrl,
}: ChooseStoreMockupSceneProps) {
  const shouldReduceMotion = useReducedMotion();

  return (
    // Fallback fill behind both layers: since the unlock scales one layer
    // out and the other in, there's a brief instant where neither fully
    // covers the frame (a scaled-down/blurred edge doesn't paint all the
    // way to the container's border) — without this, that showed through
    // as a "hole" with no background. Sampled from the lock wallpaper's
    // darkest tone so it's never actually visible, just a safety net.
    <div className="relative h-full w-full overflow-hidden bg-[#2b1330]">
      <motion.div
        className={`absolute inset-0 ${unlocked ? 'pointer-events-none' : ''}`}
        initial={false}
        animate={
          shouldReduceMotion
            ? undefined
            : unlocked
              ? { opacity: 0, scale: 1.08 }
              : { opacity: 1, scale: 1 }
        }
        transition={{ duration: 0.45, ease: 'easeInOut' }}
      >
        <LockScreenScene />
      </motion.div>

      <AnimatePresence>
        {unlocked && (
          <motion.div
            className="absolute inset-0"
            // Starts slightly *larger* than the frame (not smaller) so it
            // always fully covers it while shrinking into place — scaling
            // up from below 1 briefly reveals the frame's edges since the
            // layer is legitimately smaller than the container for that
            // instant.
            initial={shouldReduceMotion ? false : { opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              duration: 0.5,
              ease: [0.22, 1, 0.36, 1],
              delay: shouldReduceMotion ? 0 : 0.15,
            }}
          >
            <HomeScreenScene appName={appName} appIconUrl={appIconUrl} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
